import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { UI, DISPLAY } from '../../styles/tl5';

// The v5 reference's "state" card: a rounded icon tile, a title, a line
// of explanation and up to two actions.
//
// The reference collects eight of these on an Error States page. That
// page is a catalogue for the designer, not somewhere a patient would
// ever navigate, so instead of publishing it the card lives here and the
// states are rendered where they actually happen — see API-NOTES 2.44.
//
// `tone` picks the icon tile's colours, matching the reference's set.

const TONES = {
  warn:    { bg: '#FFF4CC', fg: '#8A6A00' },
  error:   { bg: '#FBE3E2', fg: '#B23B37' },
  info:    { bg: '#E6EEF9', fg: '#2B4C7E' },
  neutral: { bg: 'var(--tl-mint)', fg: 'var(--tl-teal-d)' },
  success: { bg: '#E3F5EF', fg: 'var(--tl-teal-d)' },
};

const action = (primary) => ({
  border: 0,
  borderRadius: '16px',
  p: '12px 18px',
  font: 'inherit',
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 800,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
  bgcolor: primary ? 'var(--tl-g9)' : 'var(--tl-mint)',
  color: primary ? '#FFFFFF' : 'var(--tl-g9)',
});

// actions: [{ label, to }] for a route, { label, href } for a link out,
// or { label, onClick } for a button. The first is the primary one.
const StateCard = ({ icon, tone = 'neutral', title, body, actions = [], sx }) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 2,
        minWidth: 0,
        p: 'clamp(20px, 3.4vw, 32px)',
        borderRadius: '28px',
        bgcolor: '#FFFFFF',
        ...sx,
      }}
    >
      {icon && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 56,
            height: 56,
            borderRadius: '18px',
            bgcolor: t.bg,
            color: t.fg,
            display: 'grid',
            placeItems: 'center',
            fontFamily: DISPLAY,
            fontWeight: 800,
            fontSize: 24,
          }}
        >
          {icon}
        </Box>
      )}
      <Box
        component="span"
        sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, lineHeight: 1.15, letterSpacing: '-.01em', color: 'var(--tl-g9)' }}
      >
        {title}
      </Box>
      {body && (
        <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted-2)' }}>
          {body}
        </Box>
      )}
      {actions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {actions.map((a, i) => {
            const sxA = action(i === 0);
            if (a.to) return <Box key={a.label} component={Link} to={a.to} sx={sxA}>{a.label}</Box>;
            if (a.href) return <Box key={a.label} component="a" href={a.href} sx={sxA}>{a.label}</Box>;
            return <Box key={a.label} component="button" type="button" onClick={a.onClick} sx={sxA}>{a.label}</Box>;
          })}
        </Box>
      )}
    </Box>
  );
};

export default StateCard;
